import { RefObject, useEffect } from "react";

interface UseStickyElementOptions {
    minWidth?: number;
    getBoundary: (element: HTMLElement) => HTMLElement | null;
    wrapperRef?: RefObject<HTMLElement>;
    enabled?: boolean;
}

export const getHeaderOffset = () => {
    const header = document.querySelector(
        'header, [class*="headerContainer"]',
    ) as HTMLElement;
    const headerHeight = header?.offsetHeight || 82;
    return headerHeight + 20;
};

export function useStickyElement(
    elementRef: RefObject<HTMLElement>,
    options: UseStickyElementOptions,
): void {
    const {
        minWidth = 1024,
        getBoundary,
        wrapperRef,
        enabled = true,
    } = options;

    useEffect(() => {
        if (!enabled) return;

        const container = elementRef.current;
        if (!container) return;

        if (window.innerWidth < minWidth) return;

        const boundary = getBoundary(container);
        if (!boundary) return;

        const topOffset = getHeaderOffset();

        const wrapper = wrapperRef?.current ?? null;
        const initialWidth = wrapper
            ? wrapper.getBoundingClientRect().width
            : container.getBoundingClientRect().width;

        if (wrapper) {
            wrapper.style.width = `${initialWidth}px`;
            wrapper.style.flexShrink = "0";
        }

        let ticking = false;

        const updatePosition = () => {
            const boundaryRect = boundary.getBoundingClientRect();
            const elementHeight = container.offsetHeight;

            const boundaryTop = boundaryRect.top;
            const boundaryBottom = boundaryRect.bottom;

            if (boundaryTop > topOffset) {
                // Boundary not yet reached – static
                container.style.position = "";
                container.style.top = "";
                container.style.width = "";
            } else if (boundaryBottom > elementHeight + topOffset + 40) {
                // Scrolling through – fixed below header
                container.style.position = "fixed";
                container.style.top = `${topOffset}px`;
                container.style.width = `${initialWidth}px`;
            } else {
                // Past boundary bottom – dock near bottom
                container.style.position = "absolute";
                container.style.top = `${
                    boundary.offsetHeight - elementHeight - 60
                }px`;
                container.style.width = `${initialWidth}px`;
            }

            ticking = false;
        };

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(updatePosition);
                ticking = true;
            }
        };

        boundary.style.position = "relative";
        container.style.zIndex = "50";

        updatePosition();

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
            container.style.position = "";
            container.style.top = "";
            container.style.width = "";
            if (wrapper) {
                wrapper.style.width = "";
                wrapper.style.flexShrink = "";
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, minWidth]);
}
